import { LoadState } from '@treadinc/horizon-api-spec';

import { Nullable } from '~types/Nullable';

/**
 * Shared load-phase colors — the existing pickup/dropoff SITE pin colors. The
 * site pins keep these colors; the truck pins mirror them, so a truck reads as
 * the same color as the site it is headed toward. Defined once and consumed by
 * both the site icon atlas (its canonical source) and the truck icon atlas
 * (plus the DOM-marker fallback) so they can never drift apart.
 */
export const PICKUP_PHASE_COLOR = '#2221BB'; // Blue - pickup site
export const DROPOFF_PHASE_COLOR = '#172559'; // Dark navy - dropoff site

// Brighter shades for the active/hovered site variants.
export const PICKUP_PHASE_COLOR_ACTIVE = '#4A49FF';
export const DROPOFF_PHASE_COLOR_ACTIVE = '#2A3D8F';

/**
 * Maps a ping's load state to its cycle phase, or `null` for states outside the
 * active pickup→dropoff cycle (which keep the standard pin color).
 *
 * The pickup phase runs from heading-to-pickup through loaded; the dropoff phase
 * runs from heading-to-dropoff through unloaded. `to_pickup` and `to_dropoff`
 * are intentionally kept in separate phases (unlike the replay Gantt, which
 * collapses both into one en-route bucket) so the two travel directions are
 * distinguishable on the live map.
 *
 * The input is the loosely-typed model getter (`Nullable<string>`); any value
 * outside the pickup/dropoff legs — including `created`/`completed`/`canceled`,
 * `null`, or a state the backend adds later — resolves to `null` so the pin
 * falls back to its standard color rather than crashing the map render.
 */
export function loadStateToPhase(loadState: Nullable<string>) {
  switch (loadState) {
    case LoadState.TO_PICKUP:
    case LoadState.ARRIVED_PICKUP:
    case LoadState.LOADED:
      return 'pickup';
    case LoadState.TO_DROPOFF:
    case LoadState.ARRIVED_DROPOFF:
    case LoadState.UNLOADED:
      return 'dropoff';
    default:
      return null;
  }
}
